import { useEffect, useState } from "react";
import { Network, Copy, Check, ShieldCheck, ShieldAlert } from "lucide-react";
import { serverStore } from "../../stores/server.store";
import toast from "react-hot-toast";
import { joinAddress } from "../../utils/join-address";

/**
 * Kompakte "Verbinden & Ports"-Karte fuer Minecraft-Server (analog zu Steam):
 * Join-Adresse + Host-Firewall-Status (firewalld). Zeigt bei fehlenden Ports
 * den Copy-Befehl zum Freigeben. Nutzt GET /:id/firewall.
 */
export const FirewallPortsCard = ({ serverId }: { serverId: string }) => {
  const [fw, setFw] = useState<any>(null);
  const [addrCopied, setAddrCopied] = useState(false);
  const [cmdCopied, setCmdCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    serverStore.fetchFirewall(serverId).then((d) => { if (alive) setFw(d); });
    return () => { alive = false; };
  }, [serverId]);

  if (!fw || !fw.required || fw.required.length === 0) return null;

  const server = serverStore.servers.find((s) => s.id === serverId);
  const isJava = !!server && !String(server.type || "").startsWith("steam") && server.type !== "bedrock";
  const host = server?.custom_domain || window.location.hostname;
  const port = fw.required[0]?.port;
  // Minecraft Java: Adresse IMMER mit Port (so fügen Freunde sie ein); Steam/Bedrock ohne Standard-Port.
  const address = isJava && port ? `${host}:${port}` : joinAddress(host, port, fw.defaultPort);

  const copy = async (text: string, setter: (v: boolean) => void) => {
    try { await navigator.clipboard.writeText(text); setter(true); setTimeout(() => setter(false), 1500); }
    catch { toast.error("Kopieren nicht möglich — Text manuell markieren."); }
  };

  return (
    <div className="bg-surface border border-line rounded-2xl overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-line">
        <Network size={15} className="text-ember" />
        <h3 className="text-sm font-medium text-fg">Verbinden & Ports</h3>
      </div>
      <div className="p-5 space-y-3">
        <div>
          <div className="text-xs text-fg-muted mb-1">Server-Adresse (im Spiel „Direktverbindung")</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded-lg bg-white/[0.04] border border-line px-3 py-2 font-mono text-sm text-fg select-all">{address}</code>
            <button onClick={() => copy(address, setAddrCopied)} title="Adresse kopieren" className="p-2 rounded-lg border border-line text-fg-muted hover:text-fg hover:bg-white/[0.06] transition-colors">
              {addrCopied ? <Check size={15} className="text-online" /> : <Copy size={15} />}
            </button>
          </div>
        </div>

        {fw.allOpen ? (
          <div className="flex items-center gap-2 rounded-lg bg-online/10 border border-online/20 px-3 py-2 text-xs text-online">
            <ShieldCheck size={15} className="shrink-0" /> Port {port} ist in der Host-Firewall freigegeben.
          </div>
        ) : fw.command ? (
          <div className="rounded-lg bg-warn/10 border border-warn/25 p-3 space-y-2">
            <div className="flex items-center gap-2 text-xs text-warn font-medium">
              <ShieldAlert size={15} className="shrink-0" />
              {fw.checked ? `Port ${port} ist noch nicht in der Firewall freigegeben.` : "Firewall-Status nicht prüfbar — bei Bedarf freigeben:"}
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-md bg-black/40 border border-line px-2.5 py-2 font-mono text-[11px] text-fg overflow-x-auto whitespace-nowrap">{fw.command}</code>
              <button onClick={() => copy(fw.command, setCmdCopied)} title="Befehl kopieren" className="p-2 rounded-lg border border-line text-fg-muted hover:text-fg hover:bg-white/[0.06] transition-colors shrink-0">
                {cmdCopied ? <Check size={15} className="text-online" /> : <Copy size={15} />}
              </button>
            </div>
            <p className="text-[11px] text-fg-muted">Einmal im Terminal des Servers ausführen. Zusätzlich muss der Router den Port ans Internet weiterleiten.</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default FirewallPortsCard;
